import { useDisclosure } from "@heroui/react";
import React, { useEffect } from "react";

import type { RootLoaderData } from "~/root";
import { UserProfileModal } from "./components/profile/modal";
import { SignInModal } from "./components/sign-in/modal";
import { useAuthAction } from "./data";

namespace AuthContext {
  export type Value = {
    openSignIn: () => void;
    openUserProfile: () => void;
    signOut: () => void;
    user: RootLoaderData["user"];
  };
}

const AuthContext = React.createContext<AuthContext.Value>({} as AuthContext.Value);

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider = ({
  children,
  value,
}: { children: React.ReactNode; value: AuthContext.Value["user"] }) => {
  const authAction = useAuthAction();
  const signInModal = useDisclosure({ id: "sign-in-modal" });
  const userProfileModal = useDisclosure({ id: "user-profile-modal" });

  useEffect(() => {
    if (value.isSignedIn) {
      signInModal.onClose();
    }
  }, [value]);

  return (
    <AuthContext.Provider
      value={{
        user: value,
        openSignIn: signInModal.onOpen,
        openUserProfile: userProfileModal.onOpen,
        signOut: () => authAction.signOut({ intent: "sign_out" }),
      }}
    >
      {children}
      <SignInModal
        isOpen={signInModal.isOpen}
        onOpenChange={signInModal.onOpenChange}
      />
      <UserProfileModal
        isOpen={userProfileModal.isOpen}
        onOpenChange={userProfileModal.onOpenChange}
      />
    </AuthContext.Provider>
  );
};
